import React, { memo, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Spin, Switch, Table } from 'antd';
import * as echarts from 'echarts';
import { formatCurrency } from '@wavvest/shared-utils';
import { ALLOCATION_CLASSES, type AllocationTotals } from './allocation';

const DEFAULT_TOOLTIP_COLOR = '#6B7280';
const HEX_COLOR_REGEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

type PieTooltipParams = { name: string; value: number; percent: number; color: string };

type AllocationTableRow = {
  key: string;
  assetClass: string;
  weight: string;
  value: string;
  color: string;
};

export type AllocationLoadingState = { managed: boolean; linked: boolean };

export type AllocationTabProps = {
  theme: 'light' | 'dark';
  /** Managed (Wavvest/Apex) accounts by class, or null when there are none. */
  managedAllocation: AllocationTotals | null;
  /** Linked (Plaid) accounts by class, or null when there are none. */
  linkedAllocation: AllocationTotals | null;
  /** Show the "Managed accounts only" switch. */
  showManagedOnlyToggle: boolean;
  /** Managed-account data is still loading. */
  managedLoading: boolean;
  /** Linked-account data is still loading; ignored while showing managed only. */
  linkedLoading?: boolean;
  loadingTip?: (loading: AllocationLoadingState) => string;
  /** Text above the total in the donut. */
  centerLabel?: string;
  /**
   * `compact` reads its colors and fonts from CSS variables on the chart
   * (`--wealth-prep-*`, `--investor-record-sans`); `large` is a bigger,
   * length-scaled total in Roboto.
   */
  centerStyle?: 'compact' | 'large';
};

const escapeHtml = (value: string): string =>
  value.replace(
    /[&<>"']/g,
    (char) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char] || char,
  );

const sanitizeTooltipColor = (value: string): string => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();
  return HEX_COLOR_REGEX.test(normalized) ? normalized : DEFAULT_TOOLTIP_COLOR;
};

const cssValue = (element: Element | null, variableName: string, fallback: string): string => {
  if (!element) return fallback;
  return window.getComputedStyle(element).getPropertyValue(variableName).trim() || fallback;
};

const cssNumber = (element: Element | null, variableName: string, fallback: number): number => {
  const value = parseFloat(cssValue(element, variableName, String(fallback)));
  return Number.isFinite(value) ? value : fallback;
};

const centerText = (
  element: Element | null,
  theme: 'light' | 'dark',
  label: string,
  amountText: string,
  centerStyle: 'compact' | 'large',
) => {
  if (centerStyle === 'large') {
    return [
      { text: label, fill: '#6E6E6E', fontSize: 18, fontWeight: 400 },
      {
        text: amountText,
        fill: theme === 'dark' ? '#ffffff' : '#000000',
        fontSize: amountText.length > 14 ? 24 : amountText.length > 10 ? 28 : 34,
        fontWeight: 600,
        fontFamily: 'Roboto',
      },
    ];
  }
  return [
    {
      text: label,
      fill: cssValue(
        element,
        '--wealth-prep-label-color',
        theme === 'dark' ? '#98a2b3' : '#7c889a',
      ),
      fontFamily: cssValue(
        element,
        '--investor-record-sans',
        'IBM Plex Sans, Roboto, ui-sans-serif, system-ui, -apple-system, Segoe UI, sans-serif',
      ),
      fontSize: 12,
      fontWeight: 700,
    },
    {
      text: amountText,
      fill: cssValue(
        element,
        '--wealth-prep-value-color',
        theme === 'dark' ? '#f2f4f7' : '#101827',
      ),
      fontSize: cssNumber(element, '--wealth-prep-value-size', 22),
      fontWeight: cssValue(element, '--wealth-prep-value-weight', '500'),
      fontFamily: cssValue(
        element,
        '--wealth-prep-value-font',
        'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
      ),
    },
  ];
};

const defaultLoadingTip = () => 'Loading allocation data...';

/**
 * Asset allocation donut and table for an investor's managed and linked
 * accounts, with a switch to show managed accounts only.
 */
const AllocationTab: React.FC<AllocationTabProps> = ({
  theme,
  managedAllocation,
  linkedAllocation,
  showManagedOnlyToggle,
  managedLoading,
  linkedLoading = false,
  loadingTip = defaultLoadingTip,
  centerLabel = 'Liquid Assets',
  centerStyle = 'compact',
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const [showManagedOnly, setShowManagedOnly] = useState(false);
  // The filter only applies while its switch is shown; if the last managed
  // account goes away, linked assets come back and the choice resets.
  const managedOnly = showManagedOnlyToggle && showManagedOnly;
  useEffect(() => {
    if (!showManagedOnlyToggle) setShowManagedOnly(false);
  }, [showManagedOnlyToggle]);
  const managedOnlyLabelId = useId();

  const pieChartData = useMemo(
    () =>
      ALLOCATION_CLASSES.map(({ name, color }) => {
        const managed = managedAllocation?.[name] ?? 0;
        const linked = managedOnly ? 0 : (linkedAllocation?.[name] ?? 0);
        return {
          name,
          value: (managed > 0 ? managed : 0) + (linked > 0 ? linked : 0),
          itemStyle: { color },
        };
      }),
    [managedAllocation, linkedAllocation, managedOnly],
  );
  const total = useMemo(
    () => pieChartData.reduce((sum, item) => sum + item.value, 0),
    [pieChartData],
  );

  const loadingState: AllocationLoadingState = {
    managed: managedLoading,
    linked: !managedOnly && linkedLoading,
  };
  const isLoading = loadingState.managed || loadingState.linked;

  const getPieOption = useCallback(() => {
    const [label, amount] = centerText(
      chartRef.current,
      theme,
      centerLabel,
      formatCurrency(total),
      centerStyle,
    );
    return {
      tooltip: {
        trigger: 'item',
        formatter: (params: PieTooltipParams): string => {
          const { name, value, percent, color } = params;
          const safePercent = escapeHtml(String(Number.isFinite(percent) ? percent : 0));
          return `
            <div style="font-weight: bold; margin-bottom: 4px;">${escapeHtml(String(name || ''))}</div>
            <div style="display: flex; align-items: center;">
              <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background-color: ${sanitizeTooltipColor(color)}; margin-right: 8px;"></span>
              <span>${escapeHtml(formatCurrency(value))} (${safePercent}%)</span>
            </div>
          `;
        },
      },
      legend: { show: false },
      graphic: [
        {
          type: 'group',
          left: 'center',
          top: 'center',
          children: [
            { type: 'text', style: { ...label, textAlign: 'center' }, x: 0, y: -12 },
            { type: 'text', style: { ...amount, textAlign: 'center' }, x: 0, y: 12 },
          ],
        },
      ],
      series: [
        {
          name: 'Asset Allocation',
          type: 'pie',
          radius: ['80%', '95%'],
          emphasis: { label: { show: false } },
          label: { show: false },
          data: pieChartData,
        },
      ],
    };
  }, [centerLabel, centerStyle, pieChartData, theme, total]);

  useEffect(() => {
    if (isLoading || !chartRef.current) {
      return undefined;
    }
    chartInstance.current?.dispose();
    chartInstance.current = echarts.init(chartRef.current);
    chartInstance.current.setOption(getPieOption());

    const handleResize = () => chartInstance.current?.resize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, [getPieOption, isLoading]);

  const tableData: AllocationTableRow[] = pieChartData.map((item, index) => ({
    key: index.toString(),
    assetClass: item.name,
    weight: total > 0 ? `${((item.value / total) * 100).toFixed(1)}%` : '0%',
    value: formatCurrency(item.value),
    color: item.itemStyle.color,
  }));

  const columns = [
    {
      title: 'Asset Class',
      dataIndex: 'assetClass',
      key: 'assetClass',
      render: (text: string, record: AllocationTableRow) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span
            style={{
              height: '10px',
              width: '10px',
              backgroundColor: record.color || '#ccc',
              borderRadius: '50%',
              display: 'inline-block',
              marginRight: '12px',
            }}
          ></span>
          <span>{text}</span>
        </div>
      ),
    },
    { title: 'Weight', dataIndex: 'weight', key: 'weight' },
    { title: 'Value', dataIndex: 'value', key: 'value' },
  ];

  return (
    <div className="asset-allocation-content">
      <div className="asset-allocation-title">Asset Allocation</div>
      {showManagedOnlyToggle && (
        <div className="asset-allocation-toggle">
          <Switch
            checked={showManagedOnly}
            onChange={setShowManagedOnly}
            size="small"
            aria-labelledby={managedOnlyLabelId}
          />
          <span id={managedOnlyLabelId} className="asset-allocation-toggle-label">
            Managed accounts only
          </span>
        </div>
      )}
      <div className="asset-allocation-main">
        {isLoading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '200px',
              width: '100%',
            }}
          >
            <Spin size="large" tip={loadingTip(loadingState)} />
          </div>
        ) : (
          <>
            <div className="allocation-table-container">
              <Table
                columns={columns}
                dataSource={tableData}
                pagination={false}
                size="small"
                rowKey="key"
              />
            </div>
            <div className="allocation-chart-container">
              <div
                ref={chartRef}
                className="pie-chart"
                style={{ height: '100%', width: '100%', minHeight: '250px' }}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default memo(AllocationTab);
